// ./src/components/features/Dashboard/DashboardItemTile/DashboardItemTile.tsx
// Reusable tile component for dashboard sections (tasks, documents, services).
// Renders as an individual card with icon, title, badges, subtitle, status text, and actions.
// Fully owns its own styling — parent components pass semantic types, not colors.

import React, { useCallback } from "react";
import { Paper, Group, Stack, Text, Badge, Box, Skeleton } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppButton } from "@/components/generic/AppButton";
import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

/**
 * Semantic badge types — the tile determines colors and labels internally.
 * Status badges: urgency/expiration state
 * Priority badges: task importance level
 */
export type TileBadgeStatusType =
  | "overdue"
  | "due_soon"
  | "expiring_soon"
  | "expired"
  | "upcoming"
  | "in_progress";

export type TileBadgePriorityType = "high" | "medium" | "low";

export interface TileBadge {
  type: TileBadgeStatusType | TileBadgePriorityType;
}

/**
 * Semantic action types — the tile determines icons and styling internally.
 */
export type TileActionType = "complete" | "edit" | "view";

export interface TileAction {
  type: TileActionType;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  tooltip?: string;
}

/**
 * Semantic status type for the bottom status text line.
 */
export type TileStatusType = "success" | "warning" | "error" | "info" | "neutral";

/**
 * Semantic value type for the right-side value display (e.g., amounts, distances).
 */
export type TileValueType = "expense" | "revenue" | "distance" | "neutral";

export interface DashboardItemTileProps {
  /** Item title (e.g., "Replace brake pads") */
  title: string;

  /** Icon name (Tabler icon) for the left circle */
  iconName: string;

  /** Color for the icon circle background and icon — Mantine color key */
  iconColor: string;

  /** Array of badge descriptors to display below the title */
  badges?: TileBadge[];

  /** Subtitle line (e.g., "My Chrysler · Feb 28, 2026") */
  subtitle?: string;

  /** Description line below subtitle (e.g., refuel details "15.5 L • Full tank • Premium") */
  description?: string;

  /** Bottom status text (e.g., "3 days remaining", "4 days overdue") */
  statusText?: string;

  /** Semantic type controlling status text color */
  statusType?: TileStatusType;

  /** Value text displayed on the right side (e.g., "$45.20", "+$120.00", "32.5 km") */
  valueText?: string;

  /** Semantic type controlling value text color */
  valueType?: TileValueType;

  /** Action buttons displayed on the right side (mutually exclusive with valueText) */
  actions?: TileAction[];

  /** Click handler for the entire tile (e.g., navigate to detail) */
  onClick?: () => void;
}

// ============================================
// Badge Configuration
// ============================================

interface BadgeConfig {
  color: string;
  labelKey: string;
}

const BADGE_STATUS_CONFIG: Record<TileBadgeStatusType, BadgeConfig> = {
  overdue: { color: "red", labelKey: "common:dashboard.tile.badge.overdue" },
  due_soon: { color: "orange", labelKey: "common:dashboard.tile.badge.dueSoon" },
  expiring_soon: { color: "orange", labelKey: "common:dashboard.tile.badge.expiringSoon" },
  expired: { color: "red", labelKey: "common:dashboard.tile.badge.expired" },
  upcoming: { color: "blue", labelKey: "common:dashboard.tile.badge.upcoming" },
  in_progress: { color: "blue", labelKey: "common:dashboard.tile.badge.inProgress" },
};

const BADGE_PRIORITY_CONFIG: Record<TileBadgePriorityType, BadgeConfig> = {
  high: { color: "red", labelKey: "common:dashboard.tile.badge.high" },
  medium: { color: "yellow", labelKey: "common:dashboard.tile.badge.medium" },
  low: { color: "gray", labelKey: "common:dashboard.tile.badge.low" },
};

const ALL_STATUS_TYPES: Set<string> = new Set(Object.keys(BADGE_STATUS_CONFIG));

const getBadgeConfig = (type: TileBadge["type"]): BadgeConfig => {
  if (ALL_STATUS_TYPES.has(type)) {
    return BADGE_STATUS_CONFIG[type as TileBadgeStatusType];
  }
  return BADGE_PRIORITY_CONFIG[type as TileBadgePriorityType];
};

// ============================================
// Action Configuration
// ============================================

interface ActionConfig {
  iconName: string;
  color: string;
  variant: "secondary" | "tertiary";
}

const ACTION_CONFIG: Record<TileActionType, ActionConfig> = {
  complete: { iconName: "Check", color: "brand", variant: "secondary" },
  edit: { iconName: "Edit", color: "brand", variant: "secondary" },
  view: { iconName: "ChevronRight", color: "brand", variant: "tertiary" },
};

// ============================================
// Status Text Color Mapping
// ============================================

const STATUS_TYPE_COLORS: Record<TileStatusType, string> = {
  success: "teal",
  warning: "orange",
  error: "red",
  info: "blue",
  neutral: "dimmed",
};

// ============================================
// Value Text Color Mapping
// ============================================

const VALUE_TYPE_COLORS: Record<TileValueType, string | undefined> = {
  expense: undefined,  // default text color
  revenue: "teal.7",
  distance: "grape",
  neutral: "dimmed",
};

// ============================================
// Icon Circle Size
// ============================================

const ICON_CIRCLE_SIZE = 44;
const ICON_SIZE = 22;

// ============================================
// Sub-components
// ============================================

interface TileBadgeRowProps {
  badges: TileBadge[];
}

const TileBadgeRow: React.FC<TileBadgeRowProps> = ({ badges }) => {
  const { t } = useTranslation(["dashboard"]);

  if (badges.length === 0) return null;

  return (
    <Group gap={6} wrap="wrap">
      {badges.map((badge, index) => {
        const config = getBadgeConfig(badge.type);
        return (
          <Badge
            key={`${badge.type}-${index}`}
            color={config.color}
            variant="light"
            size="xs"
            style={{ flexShrink: 0 }}
          >
            {t(config.labelKey)}
          </Badge>
        );
      })}
    </Group>
  );
};

interface TileActionsProps {
  actions: TileAction[];
}

const TileActions: React.FC<TileActionsProps> = ({ actions }) => {
  if (actions.length === 0) return null;

  return (
    <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
      {actions.map((action, index) => {
        const config = ACTION_CONFIG[action.type];
        return (
          <AppButton
            key={`${action.type}-${index}`}
            variant={config.variant}
            size="xs"
            iconName={config.iconName}
            onClick={action.onClick}
            loading={action.loading}
            disabled={action.disabled}
            tooltip={action.tooltip}
          />
        );
      })}
    </Group>
  );
};

// ============================================
// Skeleton
// ============================================

export const DashboardItemTileSkeleton: React.FC = () => {
  return (
    <Paper withBorder p="sm" radius="md">
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <Skeleton
          circle
          height={ICON_CIRCLE_SIZE}
          width={ICON_CIRCLE_SIZE}
          style={{ flexShrink: 0 }}
        />
        <Stack gap={6} style={{ flex: 1 }}>
          <Skeleton height={16} width="60%" />
          <Group gap={6}>
            <Skeleton height={18} width={70} radius="xl" />
            <Skeleton height={18} width={55} radius="xl" />
          </Group>
          <Skeleton height={12} width="50%" />
          <Skeleton height={12} width="40%" />
        </Stack>
        <Group gap={6}>
          <Skeleton height={30} width={30} radius="sm" />
          <Skeleton height={30} width={30} radius="sm" />
        </Group>
      </Group>
    </Paper>
  );
};

// ============================================
// Main Component
// ============================================

export const DashboardItemTile: React.FC<DashboardItemTileProps> = ({
  title,
  iconName,
  iconColor,
  badges = [],
  subtitle,
  description,
  statusText,
  statusType = "neutral",
  valueText,
  valueType = "neutral",
  actions = [],
  onClick,
}) => {
  const handleClick = useCallback(() => {
    onClick?.();
  }, [onClick]);

  const statusColor = STATUS_TYPE_COLORS[statusType];
  const valueColor = VALUE_TYPE_COLORS[valueType];
  const hasActions = actions.length > 0;
  const hasValue = !!valueText;

  return (
    <Paper
      withBorder
      p="sm"
      radius="md"
      onClick={onClick ? handleClick : undefined}
      style={{
        cursor: onClick ? "pointer" : "default",
        transition: "box-shadow 150ms ease, border-color 150ms ease",
      }}
      className={onClick ? "dashboard-tile-clickable" : undefined}
    >
      <Group gap="sm" wrap="nowrap" align="flex-start">
        {/* Icon Circle */}
        <Box
          style={{
            width: ICON_CIRCLE_SIZE,
            height: ICON_CIRCLE_SIZE,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
          bg={`${iconColor}.1`}
        >
          <AppIcon
            iconName={iconName}
            size={ICON_SIZE}
            color={`var(--mantine-color-${iconColor}-6)`}
          />
        </Box>

        {/* Content */}
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          {/* Title row — with value on the right when present */}
          <Group gap="xs" wrap="nowrap" justify="space-between" align="flex-start">
            <Text fw={600} size="sm" lineClamp={2} style={{ flex: 1, minWidth: 0 }}>
              {title}
            </Text>
            {hasValue && (
              <Text
                size="sm"
                fw={600}
                ff="var(--ff-mono)"
                c={valueColor}
                style={{ flexShrink: 0 }}
              >
                {valueText}
              </Text>
            )}
          </Group>

          {/* Badges Row */}
          {badges.length > 0 && <TileBadgeRow badges={badges} />}

          {/* Subtitle */}
          {subtitle && (
            <Text size="xs" c="dimmed" lineClamp={1}>
              {subtitle}
            </Text>
          )}

          {/* Description */}
          {description && (
            <Text size="xs" c="dimmed" lineClamp={1}>
              {description}
            </Text>
          )}

          {/* Status Text */}
          {statusText && (
            <Text size="xs" c={statusColor} fw={500}>
              {statusText}
            </Text>
          )}
        </Stack>

        {/* Actions — only when no value is displayed */}
        {!hasValue && hasActions && <TileActions actions={actions} />}
      </Group>
    </Paper>
  );
};